import "./index.css";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import { ToastProvider } from "./components/Toast";
import { ApiError } from "./lib/api";
import { meQueryKey } from "./lib/session";
import { ThemeProvider } from "./lib/theme";
import { router } from "./router";

/** A 401 or forced-MFA response anywhere sends the user back through the session gate. */
function handleAuthError(error: unknown, queryKey?: readonly unknown[]) {
  if (queryKey?.[0] === meQueryKey[0]) return; // the session query drives the gate itself
  if (error instanceof ApiError && (error.status === 401 || error.code === "mfa_enrollment_required")) {
    void queryClient.invalidateQueries({ queryKey: meQueryKey });
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (error, query) => handleAuthError(error, query.queryKey) }),
  mutationCache: new MutationCache({ onError: (error) => handleAuthError(error) }),
  defaultOptions: {
    queries: {
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
      refetchOnWindowFocus: true,
      staleTime: 15_000,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      /* PWA install is a progressive enhancement */
    });
  });
}
